<template>
  <div class="md-content">
    <div class="md-layout">
      <div class="md-layout-item md-medium-size-100 md-xsmall-size-100 md-size-100">
        <h1 v-if="isEditMode">
          Edit Shortcut v2
        </h1>
        <h1 v-else>
          Create Shortcut v2
        </h1>

        <md-progress-bar v-if="isLoading" />
        <md-content
          v-else
          class="block"
        >
          <h3>General info</h3>

          <compact-localized-field
            v-model="shortcut.shortcut.title"
            label="Title"
            :error="$v.shortcut.shortcut.title.$error"
          />

          <compact-localized-field
            v-model="shortcut.shortcut.description"
            label="Description"
          />

          <two-language-input
            v-model="shortcut.shortcut.subgroup"
            label="Subgroup"
            inline
            optional
          />

          <text-input
            v-model="shortcut.shortcut.slug"
            label="Shortcut slug"
            :validation="$v.shortcut.shortcut.slug"
          />

          <label class="inlineFieldLabel">Access</label>
          <md-field class="field">
            <md-select v-model="shortcut.shortcut.shortcuts_access">
              <md-option value="SHORTCUTS_ACCESS_FREE">
                Free
              </md-option>
              <md-option value="SHORTCUTS_ACCESS_PREMIUM">
                Premium
              </md-option>
            </md-select>
          </md-field>

          <md-switch v-model="shortcut.enabled">
            Enabled
          </md-switch>
          <br>
          <md-switch v-model="shortcut.sensitive">
            Sensitive
          </md-switch>

          <br>

          <string-string-map-input
            :value.sync="shortcut.shortcut.images"
            label="Images"
          />

          <text-input
            v-model="shortcut.shortcut.read_more_link"
            label="'Read more' link URL"
          />

          <localized-c-t-a-form v-model="shortcut.shortcut.cta" />
        </md-content>

        <md-content
          v-if="!isLoading"
          class="block"
        >
          <h3>Mechanics</h3>

          <shortcut-group-selector
            :initial-shortcut-groups="shortcut.shortcut_group_ids"
            no-title
            @groupListChanged="shortcut.shortcut_group_ids = $event"
          />

          <label class="inlineFieldLabel">Sequence Index</label>
          <md-field class="field">
            <md-input v-model="shortcut.sequence_index" />
          </md-field>

          <div>
            <server-bundles-selector
              :initial-server-bundles="shortcut.server_bundles"
              :no-title="true"
              @serverListChanged="shortcut.server_bundles = $event"
            />
          </div>

          <text-input
            v-model="shortcut.comment"
            label="Comment"
          />
        </md-content>

        <md-content
          v-if="!isLoading"
          class="block"
        >
          <apps-selector
            :initial-mode="shortcut.apps_access_type"
            :initial-apps="shortcut.apps"
            @appsListChanged="shortcut.apps = $event"
            @appsModeChanged="shortcut.apps_access_type = $event"
          />
        </md-content>

        <md-content
          v-if="!isLoading"
          class="block"
        >
          <country-selector-with-continents
            :initial-mode="shortcut['countries_access_type']"
            :initial-iso-codes="shortcut['countries_iso_codes']"
            @countryListChanged="shortcut.countries_iso_codes = $event"
            @modeChanged="shortcut.countries_access_type = $event"
          />
        </md-content>

        <md-content
          v-if="!isLoading"
          class="block"
        >
          <h3>Quick Action</h3>

          <compact-localized-field
            v-model="shortcut.shortcut.action.title"
            label="Title"
          />

          <text-input
            v-model="shortcut.shortcut.action.url.app"
            label="App URL"
          />
          <text-input
            v-model="shortcut.shortcut.action.url.website"
            label="Web URL"
          />
          <text-input
            v-model="shortcut.shortcut.action.url.store"
            label="Store URL"
          />
        </md-content>

        <md-content
          v-if="!isLoading"
          class="block"
        >
          <h3>Payload :shrug:</h3>
          <string-string-map-input
            :value.sync="shortcut.shortcut.payload"
          />
        </md-content>

        <div class="buttonLineWrapper">
          <md-button
            v-if="isEditMode"
            class="md-accent deleteButton"
            @click="deleteDialog.isActive = true"
          >
            Delete
          </md-button>

          <md-button
            class="md-primary submitButton"
            @click="onSubmit"
          >
            Save
          </md-button>
        </div>
      </div>
    </div>

    <md-dialog
      :md-active.sync="previewDialog.isActive"
      class="dialog-wrapper"
    >
      <pre
        style="overflow: scroll"
        v-html="previewDialog.diffView"
      />

      <md-dialog-actions>
        <md-button @click.native="previewDialog.isActive = false">
          Close
        </md-button>
        <md-button
          class="md-primary"
          @click.native="updateShortcut"
        >
          Save
        </md-button>
      </md-dialog-actions>
    </md-dialog>

    <md-dialog-confirm
      :md-active.sync="deleteDialog.isActive"
      :md-title="`Delete the '${shortcut.shortcut.slug}' shortcut?`"
      md-confirm-text="Delete"
      md-cancel-text="Cancel"
      @md-cancel="deleteDialog.isActive = false"
      @md-confirm="onDelete"
    />
  </div>
</template>
<script>
  import TwoLanguageInput from "../../components/Forms/Fields/TwoLanguageInput";
  import CompactLocalizedField from "../../components/Forms/Fields/CompactLocalizedField";
  import ServerBundlesSelector from "../../components/Forms/ServerBundlesSelector";
  import CountrySelectorWithContinents from "../../components/Forms/Fields/CountrySelectorWithContinents";
  import AppsSelector from "../../components/Forms/AppsSelector";
  import TextInput from "../../components/Forms/Fields/TextInput";
  import ShortcutGroupSelector from "../../components/Forms/shorcut/ShortcutGroupSelector";
  import StringStringMapInput from "../../components/Forms/Fields/StringStringMapInput";
  import LocalizedCTAForm from "../../components/Forms/Fields/LocalizedCTAForm";

  import { required, requiredIf } from "vuelidate/lib/validators";
  import {diff, formatters} from "jsondiffpatch";

  export default {
    name: "EditShortcutV2",
    components: {
      TwoLanguageInput, ServerBundlesSelector,
      CountrySelectorWithContinents, AppsSelector, TextInput, ShortcutGroupSelector,
      StringStringMapInput, LocalizedCTAForm, CompactLocalizedField
    },
    data() {
      return {
        isLoading: true,
        isEditMode: !this.$route.path.includes("create"),
        previewDialog: {
          fetchedJSON: {},
          isActive: false,
          preparedBody: {}
        },
        deleteDialog: {
          isActive: false
        },

        shortcut: {}
      };
    },
    mounted(){
      if (this.isEditMode) {
        this.fetchShortcut();
      } else {
        this.shortcut = this.initializeEmptyShortcut();
        this.isLoading = false;
      }
    },
    methods: {
      initializeEmptyShortcut() {
        return {
          shortcut: {
            title: {en: "", uk: ""},
            description: {en: "", uk: ""},
            subgroup: {en: "", uk: ""},
            slug: "",
            shortcuts_access: "SHORTCUTS_ACCESS_FREE",
            action: {
              title: {
                en: "",
                uk: ""
              },
              url: {
                app: "",
                website: "",
                store: ""
              }
            },
            images: {
              icon: "",
              mediumIcon: ""
            },
            read_more_link: "",
            cta: {
              disconnected: {
                en: "Activate",
                uk: "",
              },
              connecting: {
                en: "Cancel",
                uk: "",
              },
              connected: {
                en: "Deactivate",
                uk: "",
              }
            },
            payload: {},
          },

          sequence_index: "0",
          apps_access_type: "common",
          apps: [],
          countries_iso_codes: [],
          countries_access_type: "common",
          enabled: false,
          sensitive: false,
          comment: "",
          server_bundles: [],
          shortcut_group_ids: []
        };
      },
      fetchShortcut() {
        const shortcutId = this.$route.params.id;

        this.$http.get(`/v1/admin/shortcut/system/${shortcutId}`)
          .then((res) => {
            this.shortcut = res.data.shortcut;
            this.previewDialog.fetchedJSON = JSON.parse(JSON.stringify(res.data.shortcut));

            if (!this.shortcut.shortcut.action) {
              this.shortcut.shortcut.action = {
                title: {},
                url: {
                  app: "",
                  website: "",
                  store: ""
                }
              };
            }

            this.isLoading = false;
          })
          .catch(err => {
            console.error(err);
            this.$notify({
              group: "foo",
              title: "Couldn't load shortcut",
            });

            this.isLoading = false;
          });
      },
      onSubmit () {
        if (this.isEditMode) {
          this.showPreview();
        } else {
          this.createShortcut();
        }
      },
      createShortcut() {
        this.$v.shortcut.$touch();

        if (this.$v.shortcut.$error) {
          return;
        }

        this.$http.post("/v1/admin/system/shortcut/create", { system_shortcut: this.shortcut })
          .then(() => {
            this.$notify({
              group: "foo",
              title: "Shortcut created",
            });
            window.location.href = "/shortcuts/v2";
          })
          .catch(err => {
            console.error(err);
            this.$notify({
              group: "foo",
              title: "Shortcut creation failed: " + err,
            });
            this.loading = false;
          });
      },
      showPreview() {
        this.previewDialog.isActive = true;
        this.previewDialog.preparedBody =  this.shortcut;

        const d = diff(this.previewDialog.fetchedJSON, this.previewDialog.preparedBody);

        this.previewDialog.diffView = formatters.html.format(d, this.previewDialog.fetchedJSON);

        if (this.previewDialog.diffView.length === 0) {
          this.previewDialog.diffView = "No differences found\n\r" + JSON.stringify(this.fetchedJSON, null, 4);
        }
      },
      updateShortcut() {
        this.$http.put("/v1/admin/system/shortcut/update", {
          system_shortcut: this.previewDialog.preparedBody
        })
          .then(() => {
            this.$notify({
              group: "foo",
              title: "Shortcut updated",
            });
            window.location.href = "/shortcuts/v2";
          })
          .catch(err => {
            console.error(err);
            this.$notify({
              group: "foo",
              title: "Shortcut update failed: " + err,
            });
            this.loading = false;
          });
      },
      onDelete() {
        this.isLoading = true;
        this.$http.delete(`/v1/admin/shortcut/system/delete?id=${this.$route.params.id}`)
          .then(() => {
            this.$notify({
              group: "foo",
              title: "Shortcut deleted",
            });
            window.location.href = "/shortcuts/v2";
          })
          .catch(err => {
            console.error(err);
            this.$notify({
              group: "foo",
              title: "Shortcut deletion failed: " + err,
            });
            this.loading = false;
          });
      }
    },
    validations: {
      shortcut: {
        shortcut: {
          title: {
            en: { required },
            uk: { required },
          },
          slug: { required }
        },
        enabled: { required },
        sensitive: { required },
        apps_access_type: { required },
        apps: { required: requiredIf(this?.shortcut?.apps_access_type !== "common") },
        countries_access_type: { required },
        countries_iso_codes: { required: requiredIf(this?.shortcut?.countries_access_type !== "common") }
      }
    }
  };
</script>
<style scoped lang="scss">
  $content-width: 750px;

  .block {
    padding: 20px;
    max-width: $content-width;

    h3 {
      margin: 0 0 20px;
    }

    & + & {
      margin: 40px 0 0;
    }
  }

  .inlineFieldLabel {
    display: block;
    margin: 0 0 -20px;
  }

  .buttonLineWrapper {
    max-width: $content-width;

    display: grid;
    grid-template-columns: [left-col] 100px [right-col] 100px;
    justify-content: space-between;

    .submitButton {
      grid-column-start: right-col;
    }

    .deleteButton {
      background: #ff5252 !important;
    }
  }
</style>
